import { useState, useEffect, useRef } from "react";
import { 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Avatar, 
  Chip,
  Divider,
  CircularProgress
} from "@mui/material";
import { Send as SendIcon, Person as PersonIcon } from "@mui/icons-material";
import { getMessagesByTicket, addMessage } from "../Services/messagesService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/date";

export default function TicketChat({ ticketId }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const isCollaborateur = Array.isArray(user?.roles) && user.roles.some((r) => r.startsWith('ROLE_'));

  useEffect(() => {
    if (ticketId) {
      loadMessages();
    }
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await getMessagesByTicket(ticketId);
      setMessages(data);
    } catch (error) {
      console.error("Erreur lors du chargement des messages:", error);
      showToast("Erreur lors du chargement des messages", { severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      const messageData = {
        contenu: newMessage.trim(),
        ticketId: ticketId
      };

      const newMsg = await addMessage(messageData);
      setMessages(prev => [...prev, newMsg]);
      setNewMessage("");
      showToast("Message envoyé", { severity: 'success' });
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      showToast("Erreur lors de l'envoi du message", { severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
      <Box p={2} borderBottom="1px solid" borderColor="divider">
        <Typography variant="h6" gutterBottom>
          💬 Discussion du ticket
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {messages.length} message{messages.length > 1 ? 's' : ''}
        </Typography>
      </Box>

      <Box 
        ref={messagesContainerRef}
        flex={1} 
        overflow="auto" 
        p={2}
        sx={{ 
          maxHeight: 300,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
          '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '3px' }
        }}
      >
        {messages.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              Aucun message pour le moment. Commencez la discussion !
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => {
            const isFromCurrentUser = 
              (isCollaborateur && message.senderType === 'collaborateur') ||
              (!isCollaborateur && message.senderType === 'contact');
            
            return (
              <Box key={message.id} mb={2}>
                <Box 
                  display="flex" 
                  justifyContent={isFromCurrentUser ? 'flex-end' : 'flex-start'}
                  mb={1}
                >
                  <Box
                    maxWidth="70%"
                    bgcolor={isFromCurrentUser ? 'primary.main' : 'grey.100'}
                    color={isFromCurrentUser ? 'primary.contrastText' : 'text.primary'}
                    borderRadius={2}
                    p={1.5}
                    sx={{
                      borderTopLeftRadius: isFromCurrentUser ? 16 : 4,
                      borderTopRightRadius: isFromCurrentUser ? 4 : 16,
                    }}
                  >
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {message.contenu}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      display="block" 
                      mt={0.5}
                      color={isFromCurrentUser ? 'primary.contrastText' : 'text.secondary'}
                      sx={{ opacity: 0.7 }}
                    >
                      {formatMessageTime(message.dateEnvoi)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent={isFromCurrentUser ? 'flex-end' : 'flex-start'}
                  mt={0.5}
                >
                  <Avatar sx={{ width: 20, height: 20, mr: 1 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {message.senderName || 'Utilisateur'}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Divider />
      
      <Box p={2}>
        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Tapez votre message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
